<template>
  <Teleport to="body">
    <div v-if="visible" class="ck" role="region" :aria-label="tx.label">
      <div class="ck__box">
        <p class="ck__text">
          <strong>{{ tx.heading }}</strong>
          {{ tx.body }}
          <a :href="docs.cookies">{{ tx.cookies }}</a> · <a :href="docs.privacy">{{ tx.privacy }}</a>
        </p>
        <div class="ck__actions">
          <button type="button" class="ck__btn" @click="choose(false, false)">{{ tx.reject }}</button>
          <button type="button" class="ck__btn" @click="openSettings">{{ tx.settings }}</button>
          <button type="button" class="ck__btn" @click="choose(true, true)">{{ tx.accept }}</button>
        </div>
      </div>
    </div>

    <div v-if="settingsOpen" class="ck-modal" role="dialog" aria-modal="true" :aria-label="tx.settingsTitle" @keydown.esc="settingsOpen = false">
      <div class="ck-modal__box">
        <h2 class="ck-modal__title">{{ tx.settingsTitle }}</h2>
        <p class="ck-modal__lead">{{ tx.settingsLead }}</p>
        <div class="ck-row">
          <span><strong>{{ tx.necessary }}</strong><small>{{ tx.necessaryHint }}</small></span>
          <span class="ck-row__fixed">{{ tx.alwaysOn }}</span>
        </div>
        <div class="ck-row">
          <span><strong>{{ tx.analytics }}</strong><small>{{ tx.analyticsHint }}</small></span>
          <button type="button" class="ck-switch" role="switch" :aria-checked="draft.analytics ? 'true' : 'false'" :aria-label="tx.analytics" @click="draft.analytics = !draft.analytics" />
        </div>
        <div class="ck-row">
          <span><strong>{{ tx.ads }}</strong><small>{{ tx.adsHint }}</small></span>
          <button type="button" class="ck-switch" role="switch" :aria-checked="draft.ads ? 'true' : 'false'" :aria-label="tx.ads" @click="draft.ads = !draft.ads" />
        </div>
        <div class="ck__actions">
          <button type="button" class="ck__btn" @click="choose(false, false)">{{ tx.reject }}</button>
          <button type="button" class="ck__btn" @click="choose(draft.analytics, draft.ads)">{{ tx.save }}</button>
          <button type="button" class="ck__btn" @click="choose(true, true)">{{ tx.accept }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
// Cookie consent for the public web pages (Irish DPC guidance, GDPR + ePrivacy):
// nothing optional runs before a choice; «Reject all» is as easy as «Accept all»;
// analytics and advertising are separate and off by default; the choice is kept
// for 6 months, then asked again; window.openCookieSettings() reopens it.
// Trackers must wait for consent: read window.helferConsent or listen for the
// 'helfer-consent' event ({ analytics, ads }).
import { computed, onMounted, reactive, ref } from 'vue';

const KEY = 'helfer-cookie-consent';
const VERSION = 2;
const MAX_AGE = 182 * 86400000;
const visible = ref(false);
const settingsOpen = ref(false);
const draft = reactive({ analytics: false, ads: false });

const TEXT = {
  en: {
    label: 'Cookie consent', heading: 'Cookies.', body: 'We use necessary cookies to make this site work. With your permission we would also use analytics cookies to improve it and advertising cookies (Meta, Google) to measure our ads. Nothing optional is set until you choose.',
    cookies: 'Cookie policy', privacy: 'Privacy', reject: 'Reject all', settings: 'Settings', accept: 'Accept all', save: 'Save choices',
    settingsTitle: 'Cookie settings', settingsLead: 'Choose what we may use. You can change it any time with «Cookie settings» at the bottom of the page.',
    necessary: 'Necessary', necessaryHint: 'Keep the site working and remember this choice.', alwaysOn: 'Always on',
    analytics: 'Analytics', analyticsHint: 'Count visits and see which pages help.', ads: 'Advertising', adsHint: 'Meta Pixel and Google Ads measure which ads bring visitors.',
  },
  ru: {
    label: 'Согласие на cookies', heading: 'Cookies.', body: 'Необходимые cookies нужны для работы сайта. С вашего разрешения мы также используем аналитические cookies, чтобы улучшать сайт, и рекламные (Meta, Google), чтобы оценивать нашу рекламу. Пока вы не выбрали, ничего необязательного не включается.',
    cookies: 'Политика cookies', privacy: 'Конфиденциальность', reject: 'Отклонить все', settings: 'Настроить', accept: 'Принять все', save: 'Сохранить выбор',
    settingsTitle: 'Настройки cookies', settingsLead: 'Выберите, что нам можно использовать. Изменить выбор можно в любой момент ссылкой «Настройки cookies» внизу страницы.',
    necessary: 'Необходимые', necessaryHint: 'Обеспечивают работу сайта и запоминают этот выбор.', alwaysOn: 'Всегда включены',
    analytics: 'Аналитика', analyticsHint: 'Подсчёт посещений и полезных страниц.', ads: 'Реклама', adsHint: 'Meta Pixel и Google Ads оценивают, какая реклама приводит посетителей.',
  },
};
const lang = computed(() => {
  const l = (typeof document !== 'undefined' && document.documentElement.lang) || (typeof navigator !== 'undefined' && navigator.language) || 'en';
  return /^(ru|uk)/i.test(l) ? 'ru' : 'en';
});
const tx = computed(() => TEXT[lang.value]);
// Cafe Local pages point to Cafe Local's documents; the Helfer pages keep theirs.
const docs = computed(() => (typeof window !== 'undefined' && window.location.pathname.startsWith('/cafe-local')
  ? { cookies: '/cafe-local/cookies', privacy: '/cafe-local/privacy' }
  : { cookies: '/cookies', privacy: '/privacy' }));

type Consent = { necessary: true; analytics: boolean; ads: boolean; ts: number; v: number };
function publish(c: Consent | null) {
  const w = window as unknown as { helferConsent?: Consent | null };
  w.helferConsent = c;
  window.dispatchEvent(new CustomEvent('helfer-consent', { detail: c }));
}
function read(): Consent | null {
  try {
    const c = JSON.parse(window.localStorage.getItem(KEY) || 'null');
    if (!c || c.v !== VERSION || !(Date.now() - Number(c.ts) < MAX_AGE)) return null;
    return c;
  } catch { return null; }
}
function choose(analytics: boolean, ads: boolean) {
  const c: Consent = { necessary: true, analytics, ads, ts: Date.now(), v: VERSION };
  try { window.localStorage.setItem(KEY, JSON.stringify(c)); } catch { /* private mode: ask again next time */ }
  visible.value = false;
  settingsOpen.value = false;
  publish(c);
}
function openSettings() {
  const c = read();
  draft.analytics = !!c?.analytics;
  draft.ads = !!c?.ads;
  visible.value = false;
  settingsOpen.value = true;
}

onMounted(() => {
  (window as unknown as { openCookieSettings?: () => void }).openCookieSettings = openSettings;
  // Never in the desktop app, nor on the venue's own screens (guest menu, staff):
  // they keep only what is strictly needed on the device and have no trackers.
  if ((window as unknown as { __CAFE_DESKTOP_RUNTIME__?: unknown }).__CAFE_DESKTOP_RUNTIME__) return;
  if (/^\/(v2|table|admin|kitchen|storefront|desktop-entry|login|old)(\/|$)/i.test(window.location.pathname)) return;
  const c = read();
  publish(c);
  if (!c) visible.value = true;
});
</script>

<style scoped>
.ck { position: fixed; left: 12px; right: 12px; bottom: 12px; z-index: 2147483646; display: flex; justify-content: center; font-family: Onest, system-ui, -apple-system, 'Segoe UI', sans-serif; }
.ck__box { width: 100%; max-width: 980px; box-sizing: border-box; background: #FFFFFF; color: #2A211B; border-radius: 18px; box-shadow: 0 16px 50px rgba(0, 0, 0, 0.28); padding: 18px 20px; display: flex; flex-wrap: wrap; gap: 14px 20px; align-items: center; }
.ck__text { margin: 0; flex: 1 1 380px; font-size: 14.5px; line-height: 1.55; }
.ck__text a { color: #0F5A43; font-weight: 600; }
.ck__actions { display: flex; flex-wrap: wrap; gap: 10px; flex: 1 1 400px; }
/* Same size, same colour: rejecting is exactly as easy as accepting. */
.ck__btn { flex: 1 1 120px; min-height: 46px; padding: 0 16px; border-radius: 999px; border: 2px solid #0F5A43; background: #FFFFFF; color: #0F5A43; font: 600 15px Onest, system-ui, sans-serif; cursor: pointer; }
.ck__btn:hover { background: #EEF5F1; }
.ck__btn:focus-visible, .ck-switch:focus-visible { outline: 3px solid #C9A56A; outline-offset: 2px; }
.ck-modal { position: fixed; inset: 0; z-index: 2147483647; background: rgba(14, 30, 24, 0.55); display: flex; align-items: center; justify-content: center; padding: 16px; font-family: Onest, system-ui, sans-serif; }
.ck-modal__box { width: 100%; max-width: 540px; max-height: 90vh; overflow-y: auto; box-sizing: border-box; background: #FFFFFF; color: #2A211B; border-radius: 20px; padding: 24px; display: flex; flex-direction: column; gap: 12px; }
.ck-modal__title { margin: 0; font-family: 'Playfair Display', Georgia, serif; font-weight: 400; font-size: 28px; }
.ck-modal__lead { margin: 0; font-size: 14.5px; line-height: 1.55; color: #4E4034; }
.ck-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 12px 0; border-top: 1px solid #E5DCCD; }
.ck-row > span:first-child { display: flex; flex-direction: column; gap: 3px; }
.ck-row small { font-size: 13.5px; color: #5E5246; line-height: 1.45; }
.ck-row__fixed { font-size: 13.5px; font-weight: 600; color: #0F5A43; white-space: nowrap; }
.ck-switch { flex-shrink: 0; position: relative; width: 52px; height: 30px; border-radius: 999px; border: 0; background: #BDB3A6; cursor: pointer; }
.ck-switch::after { content: ''; position: absolute; top: 3px; left: 3px; width: 24px; height: 24px; border-radius: 50%; background: #FFFFFF; transition: left 0.15s; }
.ck-switch[aria-checked='true'] { background: #0F5A43; }
.ck-switch[aria-checked='true']::after { left: 25px; }
</style>
